'use client';

import Link from 'next/link';
import { createPortal } from 'react-dom';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { LucideIcon, Menu, ChevronLeft, ChevronDown } from 'lucide-react';

export interface SidebarNavItem {
    name: string;
    href: string;
    icon: LucideIcon | React.ComponentType<{ size?: number; className?: string }>;
    label?: string;
    /** Announced but not built yet — shown greyed out and not clickable. */
    disabled?: boolean;
    /** Makes this item a group: no page of its own, it expands into these links. */
    children?: SidebarNavItem[];
}

interface Props {
    links: SidebarNavItem[];
    isOpen: boolean;
    onToggle: () => void;
    onClose: () => void;
    variant?: 'default' | 'admin';
}

const groupStorageKey = (name: string) => `sidebar_group_${name.toLowerCase()}`;

function Badge({ link }: { link: SidebarNavItem }) {
    if (!link.label) return null;
    return (
        <span
            className={[
                'ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none whitespace-nowrap',
                link.disabled
                    ? 'bg-gray-700 text-gray-300'
                    : 'bg-gradient-to-br from-emerald-500 to-emerald-800 text-white',
            ].join(' ')}
        >
            {link.label}
        </span>
    );
}

export default function LeftSidebar({ links, isOpen, onToggle, onClose, variant = 'default' }: Props) {
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const [hovered, setHovered] = useState<{ name: string; top: number; left: number } | null>(null);
    // Groups the user has collapsed — everything else starts expanded, so the
    // children (and their icons/badges) are visible without an extra click.
    const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
    // Collapsed rail: the group whose children are shown in a flyout next to it.
    const [flyout, setFlyout] = useState<{ group: SidebarNavItem; top: number; left: number } | null>(null);
    const flyoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Exposed so other fixed elements (e.g. the Matrix toggle) can center
    // themselves on the rail regardless of its current collapsed/expanded width.
    useEffect(() => {
        document.documentElement.style.setProperty('--sidebar-rail-w', isOpen ? '14rem' : '4rem');
    }, [isOpen]);

    // Labels are already visible when expanded — the tooltip/flyout only matter
    // for the icon-only collapsed rail, so drop any stale one on expand.
    useEffect(() => {
        if (isOpen) {
            setHovered(null);
            setFlyout(null);
        }
    }, [isOpen]);

    useEffect(() => {
        setFlyout(null);
    }, [pathname]);

    useEffect(() => {
        try {
            const stored: Record<string, boolean> = {};
            links.filter(l => l.children).forEach(l => {
                stored[l.name] = localStorage.getItem(groupStorageKey(l.name)) === '0';
            });
            setCollapsedGroups(stored);
        } catch {
            // storage unavailable — keep every group expanded
        }
    }, [links]);

    const showTooltip = (name: string) => (e: React.MouseEvent | React.FocusEvent) => {
        if (isOpen) return;
        const rect = e.currentTarget.getBoundingClientRect();
        setHovered({ name, top: rect.top + rect.height / 2, left: rect.right + 14 });
    };
    const hideTooltip = () => setHovered(null);

    const openFlyout = (group: SidebarNavItem) => (e: React.MouseEvent | React.FocusEvent) => {
        if (isOpen) return;
        if (flyoutTimer.current) clearTimeout(flyoutTimer.current);
        const rect = e.currentTarget.getBoundingClientRect();
        setHovered(null);
        setFlyout({ group, top: rect.top, left: rect.right + 14 });
    };
    // Small delay so the pointer can travel from the rail icon into the flyout.
    const scheduleFlyoutClose = () => {
        if (flyoutTimer.current) clearTimeout(flyoutTimer.current);
        flyoutTimer.current = setTimeout(() => setFlyout(null), 150);
    };
    const keepFlyout = () => {
        if (flyoutTimer.current) clearTimeout(flyoutTimer.current);
    };

    const isActive = (href: string) => {
        if (href.startsWith('/leaderboard')) return pathname.startsWith('/leaderboard');
        const [hrefPath, hrefQuery] = href.split('?');
        if (hrefPath !== pathname) return false;
        const hrefTab = new URLSearchParams(hrefQuery).get('tab');
        return hrefTab === searchParams.get('tab');
    };

    const closeOnMobile = () => {
        // Close only on mobile (overlay covers content there)
        if (typeof window !== 'undefined' && window.innerWidth < 1024) onClose();
    };

    const toggleGroup = (name: string) => {
        setCollapsedGroups(prev => {
            const next = { ...prev, [name]: !prev[name] };
            try {
                localStorage.setItem(groupStorageKey(name), next[name] ? '0' : '1');
            } catch {
                // ignore — the toggle still works for this session
            }
            return next;
        });
    };

    const renderLink = (link: SidebarNavItem, nested = false) => {
        const Icon = link.icon;
        const active = isActive(link.href);

        const baseClass = [
            'flex items-center rounded-lg my-0.5 transition-all duration-150',
            nested ? 'h-9 mr-2 px-3' : 'h-11 mx-2',
            !nested && (isOpen ? 'px-3' : 'justify-center'),
        ].filter(Boolean).join(' ');

        const content = (
            <>
                <Icon size={nested ? 17 : 19} className="shrink-0" />
                {isOpen && (
                    <>
                        <span className={`ml-3 whitespace-nowrap font-medium ${nested ? 'text-[13px]' : 'text-sm'}`}>
                            {link.name}
                        </span>
                        <Badge link={link} />
                    </>
                )}
            </>
        );

        // Announced-but-unbuilt pages have no route to go to yet.
        if (link.disabled) {
            return (
                <div
                    key={link.href}
                    aria-disabled="true"
                    aria-label={`${link.name} — ${link.label ?? 'Soon'}`}
                    onMouseEnter={showTooltip(`${link.name} — ${link.label ?? 'Soon'}`)}
                    onMouseLeave={hideTooltip}
                    className={`${baseClass} text-gray-500 cursor-default`}
                >
                    {content}
                </div>
            );
        }

        return (
            <Link
                key={link.href}
                href={link.href}
                onClick={closeOnMobile}
                aria-label={link.name}
                onMouseEnter={showTooltip(link.name)}
                onMouseLeave={hideTooltip}
                onFocus={showTooltip(link.name)}
                onBlur={hideTooltip}
                className={[
                    baseClass,
                    active
                        ? nested ? 'text-green-400 bg-green-500/10' : 'text-green-400'
                        : 'text-gray-200 hover:text-green-400',
                ].join(' ')}
            >
                {content}
            </Link>
        );
    };

    const renderGroup = (group: SidebarNavItem) => {
        const Icon = group.icon;
        const children = group.children ?? [];
        const childActive = children.some(c => isActive(c.href));
        // A group holding the current page always shows it, even if collapsed before.
        const expanded = childActive || !collapsedGroups[group.name];
        const hasNew = children.some(c => c.label && !c.disabled);
        const flyoutOpen = flyout?.group.name === group.name;

        if (!isOpen) {
            // Collapsed rail: icon only, children open in a flyout on hover/click.
            return (
                <button
                    key={group.name}
                    type="button"
                    aria-label={group.name}
                    aria-haspopup="menu"
                    aria-expanded={flyoutOpen}
                    onMouseEnter={openFlyout(group)}
                    onMouseLeave={scheduleFlyoutClose}
                    onFocus={openFlyout(group)}
                    onClick={(e) => (flyoutOpen ? setFlyout(null) : openFlyout(group)(e))}
                    className={[
                        'relative flex items-center justify-center h-11 w-[calc(100%-1rem)] mx-2 my-0.5 rounded-lg transition-all duration-150',
                        childActive || flyoutOpen ? 'text-green-400' : 'text-gray-200 hover:text-green-400',
                    ].join(' ')}
                >
                    <Icon size={19} className="shrink-0" />
                    {/* Hint that something new sits inside the group */}
                    {hasNew && <span className="absolute top-2.5 right-3.5 w-1.5 h-1.5 rounded-full bg-emerald-400" />}
                </button>
            );
        }

        return (
            <div key={group.name}>
                <button
                    type="button"
                    onClick={() => toggleGroup(group.name)}
                    aria-expanded={expanded}
                    className={[
                        'flex items-center h-11 w-[calc(100%-1rem)] mx-2 my-0.5 px-3 rounded-lg transition-all duration-150',
                        childActive ? 'text-green-400' : 'text-gray-200 hover:text-green-400',
                    ].join(' ')}
                >
                    <Icon size={19} className="shrink-0" />
                    <span className="ml-3 text-sm font-medium whitespace-nowrap">{group.name}</span>
                    {!expanded && hasNew && <span className="ml-2 w-1.5 h-1.5 rounded-full bg-emerald-400" />}
                    <ChevronDown
                        size={16}
                        className={`ml-auto shrink-0 text-gray-500 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
                    />
                </button>

                {/* Children — indented on a guide line, each keeping its own icon */}
                <div className={`grid transition-[grid-template-rows] duration-200 ease-out ${expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                    <div className="overflow-hidden">
                        <div className="ml-[1.35rem] pl-2 border-l border-white/10">
                            {children.map(child => renderLink(child, true))}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            <aside
                className={[
                    'fixed left-0 top-0 h-full z-50 flex flex-col',
                    'shadow-sm transition-all duration-300 ease-in-out overflow-hidden',
                    variant === 'admin'
                        ? 'bg-gray-900 border-r border-gray-800'
                        : 'backdrop-blur border-r border-green-200/20',
                    isOpen
                        ? 'w-56 translate-x-0'
                        : 'lg:w-16 lg:translate-x-0 w-56 -translate-x-full',
                ].join(' ')}
            >
                {/* Top: expand / collapse toggle — same height as navbar */}
                <button
                    onClick={onToggle}
                    title={isOpen ? 'Collapse menu' : 'Expand menu'}
                    className={[
                        'h-16 shrink-0 flex items-center',
                        'text-gray-400 hover:text-gray-100 transition-colors',
                        isOpen ? 'px-4 gap-3' : 'justify-center',
                    ].join(' ')}
                >
                    {isOpen
                        ? <ChevronLeft size={20} />
                        : <Menu size={20} />
                    }
                    {isOpen && (
                        <span className="text-sm font-bold tracking-widest text-white whitespace-nowrap">
                            Menu
                        </span>
                    )}
                </button>

                {/* Nav links */}
                <nav className="flex-1 py-3 overflow-y-auto overflow-x-hidden">
                    {links.map(link => (link.children ? renderGroup(link) : renderLink(link)))}
                </nav>
            </aside>

            {hovered && typeof document !== 'undefined' && createPortal(
                <div
                    className="fixed z-[200] -translate-y-1/2 px-2.5 py-1.5 rounded-lg bg-gray-800 border border-gray-700 text-xs font-medium text-gray-100 whitespace-nowrap shadow-lg pointer-events-none"
                    style={{ top: hovered.top, left: hovered.left }}
                >
                    {hovered.name}
                </div>,
                document.body
            )}

            {flyout && typeof document !== 'undefined' && createPortal(
                <div
                    role="menu"
                    onMouseEnter={keepFlyout}
                    onMouseLeave={scheduleFlyoutClose}
                    className="fixed z-[200] min-w-48 rounded-xl bg-gray-800 border border-gray-700 shadow-2xl p-1.5"
                    style={{ top: flyout.top, left: flyout.left }}
                >
                    <div className="px-2.5 pt-1.5 pb-2 text-[10px] font-bold uppercase tracking-widest text-gray-400">
                        {flyout.group.name}
                    </div>
                    {(flyout.group.children ?? []).map(child => {
                        const Icon = child.icon;
                        const active = isActive(child.href);
                        return (
                            <Link
                                key={child.href}
                                href={child.href}
                                role="menuitem"
                                onClick={() => setFlyout(null)}
                                className={[
                                    'flex items-center gap-3 h-9 px-2.5 rounded-lg text-sm font-medium transition-colors',
                                    active ? 'text-green-400 bg-green-500/10' : 'text-gray-100 hover:text-green-400 hover:bg-white/5',
                                ].join(' ')}
                            >
                                <Icon size={17} className="shrink-0" />
                                <span className="whitespace-nowrap">{child.name}</span>
                                <Badge link={child} />
                            </Link>
                        );
                    })}
                </div>,
                document.body
            )}
        </>
    );
}
